/**
 * Phone numbers checked by the COUNTRY they belong to — the twin of the
 * phone app's lib/utils/phone_rules.dart (Gabriel, 2026-09-30: "anywhere we
 * are putting a number, it has to have rules for that specific country …
 * absurd numbers shouldn't be allowed, same for same numbers"). Keep the two
 * in step: same rules, same messages.
 */
export interface PhoneRule {
  dial: string;
  iso: string;
  /** Digits after the country code, every length a real number can have. */
  lengths: number[];
  /** First digit(s) a mobile can start with (after the code, no trunk 0). */
  leading: string[];
  example: string;
}

export const PHONE_RULES: PhoneRule[] = [
  { dial: '+254', iso: 'KE', lengths: [9], leading: ['7', '1'], example: '722 000 000' },
  { dial: '+256', iso: 'UG', lengths: [9], leading: ['7', '3', '4'], example: '772 000 000' },
  { dial: '+255', iso: 'TZ', lengths: [9], leading: ['6', '7'], example: '712 000 000' },
  { dial: '+250', iso: 'RW', lengths: [9], leading: ['7'], example: '780 000 000' },
  { dial: '+251', iso: 'ET', lengths: [9], leading: ['9', '7'], example: '911 000 000' },
  { dial: '+234', iso: 'NG', lengths: [10], leading: ['7', '8', '9'], example: '803 000 0000' },
  { dial: '+27', iso: 'ZA', lengths: [9], leading: ['6', '7', '8'], example: '821 000 000' },
  { dial: '+1', iso: 'US', lengths: [10], leading: ['2', '3', '4', '5', '6', '7', '8', '9'], example: '415 555 0100' },
  { dial: '+44', iso: 'GB', lengths: [10], leading: ['7'], example: '7700 900000' },
];

export function phoneRuleFor(dial: string): PhoneRule | null {
  const d = (dial || '').trim();
  return PHONE_RULES.find((r) => r.dial === d) || null;
}

/** Digits after the country code: strips a leading code and trunk zeros. */
export function nationalDigits(raw: string, dial: string): string {
  let digits = (raw || '').replace(/[^0-9]/g, '');
  const code = (dial || '').replace(/[^0-9]/g, '');
  const rule = phoneRuleFor(dial);
  const maxLen = rule ? Math.max(...rule.lengths) : 12;
  /* The code is stripped only when it was clearly typed as a code: a leading
     "+", or more digits than any national number has. A US number that simply
     starts with 1, or a Kenyan one starting 25…, is left alone. */
  const typedWithPlus = (raw || '').trim().startsWith('+');
  if (code && digits.startsWith(code) && (typedWithPlus || digits.length > maxLen)) digits = digits.slice(code.length);
  while (digits.startsWith('0')) digits = digits.slice(1);
  return digits;
}

/** Every digit the same, seven+ of one digit in a row, or a straight run. */
export function looksAbsurd(digits: string): boolean {
  if (digits.length < 7) return false;
  if (/^(\d)\1+$/.test(digits)) return true;
  if (/(\d)\1{6,}/.test(digits)) return true;
  const up = '01234567890123456789';
  const down = '98765432109876543210';
  return up.includes(digits) || down.includes(digits);
}

/** Null when the number is fine; otherwise one plain sentence for the field. */
export function phoneProblem(raw: string, dial: string): string | null {
  const rule = phoneRuleFor(dial);
  const digits = nationalDigits(raw, dial);
  if (!digits) return 'Enter a phone number.';
  if (!rule) {
    if (digits.length < 7 || digits.length > 12) return 'That does not look like a phone number.';
    if (looksAbsurd(digits)) return 'That does not look like a real number.';
    return null;
  }
  if (!rule.lengths.includes(digits.length)) {
    const want = rule.lengths.length === 1 ? `${rule.lengths[0]} digits` : `${rule.lengths.join(' or ')} digits`;
    return `A ${rule.iso} number is ${want} after ${rule.dial} — e.g. ${rule.example}.`;
  }
  if (rule.leading.length && !rule.leading.some((l) => digits.startsWith(l))) {
    return `A ${rule.iso} mobile number starts with ${rule.leading.join(' or ')} — e.g. ${rule.example}.`;
  }
  if (looksAbsurd(digits)) return 'That does not look like a real number.';
  return null;
}

export const isValidPhone = (raw: string, dial: string): boolean => phoneProblem(raw, dial) === null;

/** "+254722000000" — the stored form; null when not valid. */
export function phoneE164(raw: string, dial: string): string | null {
  return isValidPhone(raw, dial) ? `${dial.trim()}${nationalDigits(raw, dial)}` : null;
}
